<script setup>
import { ref, computed, defineProps } from 'vue';

// Props
const props = defineProps({
  // Tooltip content - can be string or object with structured content
  content: {
    type: [String, Object],
    required: true
  },
  // Position preference: 'top', 'bottom', 'left', 'right', 'auto'
  position: {
    type: String,
    default: 'top'
  },
  // Icon type: 'question', 'info', 'warning'
  iconType: {
    type: String,
    default: 'question',
    validator: (value) => ['question', 'info', 'warning'].includes(value)
  },
  // Size of the icon
  size: {
    type: String,
    default: 'sm'
  },
  // Maximum width of tooltip
  maxWidth: {
    type: String,
    default: '300px'
  },
  // Delay before showing tooltip (in ms)
  showDelay: {
    type: Number,
    default: 0
  },
  // Delay before hiding tooltip (in ms)
  hideDelay: {
    type: Number,
    default: 100
  }
});

// Reactive state
const showTooltip = ref(false);
const tooltipPosition = ref({ x: 0, y: 0 });
const actualPosition = ref(props.position);
const showTimeout = ref(null);
const hideTimeout = ref(null);

// Computed properties
const iconClasses = computed(() => {
  const baseClasses = 'text-gray-400 hover:text-blue-500 transition-colors duration-200 cursor-default';
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };
  return `${baseClasses} ${sizeClasses[props.size] || sizeClasses.sm}`;
});

const iconName = computed(() => {
  const icons = {
    question: ['far', 'circle-question'],
    info: ['fas', 'info'],
    warning: ['fas', 'exclamation-triangle']
  };
  return icons[props.iconType] || icons.question;
});

const tooltipClasses = computed(() => {
  const baseClasses = 'fixed z-[60] bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg border border-gray-700 pointer-events-none';
  const positionClasses = {
    top: 'transform -translate-x-1/2 -translate-y-full',
    bottom: 'transform -translate-x-1/2 translate-y-2',
    left: 'transform -translate-x-full -translate-y-1/2',
    right: 'transform translate-x-2 -translate-y-1/2'
  };
  return `${baseClasses} ${positionClasses[actualPosition.value] || positionClasses.top}`;
});

const tooltipStyle = computed(() => ({
  left: `${tooltipPosition.value.x}px`,
  top: `${tooltipPosition.value.y}px`,
  maxWidth: props.maxWidth,
  display: showTooltip.value ? 'block' : 'none'
}));

const arrowClasses = computed(() => {
  const positions = {
    top: 'absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900',
    bottom: 'absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900',
    left: 'absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-l-4 border-transparent border-l-gray-900',
    right: 'absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900'
  };
  return positions[actualPosition.value] || positions.top;
});

// Methods
const calculatePosition = (element) => {
  const rect = element.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  
  let x, y;
  let position = props.position;
  
  // Auto-positioning logic to avoid viewport edges
  if (position === 'auto') {
    // const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Determine best position based on available space
    if (rect.top > 100) position = 'top';
    else if (rect.bottom < viewportHeight - 100) position = 'bottom';
    else if (rect.left > 200) position = 'left';
    else position = 'right';
  }
  
  actualPosition.value = position;
  
  switch (position) {
    case 'top':
      x = rect.left + rect.width / 2 + scrollLeft;
      y = rect.top + scrollTop - 10;
      break;
    case 'bottom':
      x = rect.left + rect.width / 2 + scrollLeft;
      y = rect.bottom + scrollTop + 10;
      break;
    case 'left':
      x = rect.left + scrollLeft - 10;
      y = rect.top + rect.height / 2 + scrollTop;
      break;
    case 'right':
      x = rect.right + scrollLeft + 10;
      y = rect.top + rect.height / 2 + scrollTop;
      break;
    default:
      x = rect.left + rect.width / 2 + scrollLeft;
      y = rect.top + scrollTop - 10;
  }
  
  tooltipPosition.value = { x, y };
};

const showTooltipHandler = (event) => {
  clearTimeout(hideTimeout.value);
  
  if (props.showDelay > 0) {
    showTimeout.value = setTimeout(() => {
      calculatePosition(event.target);
      showTooltip.value = true;
    }, props.showDelay);
  } else {
    calculatePosition(event.target);
    showTooltip.value = true;
  }
};

const hideTooltipHandler = () => {
  clearTimeout(showTimeout.value);
  
  if (props.hideDelay > 0) {
    hideTimeout.value = setTimeout(() => {
      showTooltip.value = false;
    }, props.hideDelay);
  } else {
    showTooltip.value = false;
  }
};

// Cleanup timeouts on unmount
import { onUnmounted } from 'vue';
onUnmounted(() => {
  clearTimeout(showTimeout.value);
  clearTimeout(hideTimeout.value);
});
</script>

<template>
  <div class="inline-block relative">
    <!-- Info Icon -->
    <font-awesome-icon 
      :icon="iconName"
      :class="iconClasses"
      @mouseenter="showTooltipHandler"
      @mouseleave="hideTooltipHandler"
      @focus="showTooltipHandler"
      @blur="hideTooltipHandler"
      @touchstart="showTooltipHandler"
      @touchend="hideTooltipHandler"
      tabindex="0"
      role="button"
      :aria-label="`Show help information: ${typeof content === 'string' ? content.substring(0, 50) : 'Help information'}`"
    />
    
    <!-- Tooltip Portal (rendered outside component tree to avoid z-index issues) -->
    <Teleport to="body">
      <div 
        v-if="showTooltip"
        :class="tooltipClasses"
        :style="tooltipStyle"
        role="tooltip"
        :aria-hidden="!showTooltip"
      >
        <!-- String content -->
        <div v-if="typeof content === 'string'" class="text-left whitespace-pre-line">
          {{ content }}
        </div>
        
        <!-- Structured object content -->
        <div v-else-if="typeof content === 'object'" class="space-y-1 text-left">
          <div v-for="(value, key) in content" :key="key">
            <p><strong>{{ key }}:</strong> {{ value }}</p>
          </div>
        </div>
        
        <!-- Tooltip arrow -->
        <div :class="arrowClasses"></div>
      </div>
    </Teleport>
  </div>
</template>
